/**
 * Occurrence data source for the site scheduling calendar.
 *
 * Owns fetching server-expanded occurrences for the visible date window and
 * turning them into MUI X Scheduler events. It fetches (debounced) only when the
 * visible date moves outside the already-fetched window, and exposes an
 * immediate/debounced reload the write path calls after a mutation so the grid
 * reflects the new schedule. The whole event series is loaded alongside, since
 * the event dialog reads recurrence descriptors from it.
 *
 * Contains:
 * - useCalendarOccurrences — hook (default export) returning
 *   { events, occurrences, timezone, loadData, loadDataDebounced }
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchCalendarOccurrences, loadCalendarSeries } from '@sophocles/features/calendar/api';
import {
  selectCalendarOccurrences,
  selectCalendarTimezone,
  selectFetchedWindow,
} from '@sophocles/features/calendar/slice';
import { computeOccurrenceWindow, isWindowCovered } from '@sophocles/views/sites/utils/calendarWindow';
import { occurrencesToEvents } from '@sophocles/views/sites/utils/schedulerEventAdapter';

const OCCURRENCE_FETCH_DEBOUNCE_MS = 300;

/**
 * @param {object} params
 * @param {number} params.siteId
 * @param {Date} params.visibleDate - The scheduler's current visible date.
 * @param {number} [params.weekStartsOn=0] - date-fns week start (0 = Sunday).
 * @param {Map<any, string>} [params.resourceIdByRelayId] - relay id -> resource
 *   id, so each occurrence is placed on its relay's resource row.
 * @returns {{events: object[], occurrences: object[], timezone: ?string, loadData: Function, loadDataDebounced: Function}}
 */
export default function useCalendarOccurrences({ siteId, visibleDate, weekStartsOn = 0, resourceIdByRelayId }) {
  const dispatch = useDispatch();
  const occurrences = useSelector(selectCalendarOccurrences);
  const timezone = useSelector(selectCalendarTimezone);
  const fetchedWindow = useSelector(selectFetchedWindow);

  const neededWindow = useMemo(() => computeOccurrenceWindow(visibleDate, weekStartsOn), [visibleDate, weekStartsOn]);

  const fetchWindow = useCallback(
    window => {
      if (siteId != null) {
        dispatch(fetchCalendarOccurrences({ siteId, start: window.start, end: window.end }));
      }
    },
    [dispatch, siteId],
  );

  const loadData = useCallback(() => {
    if (siteId != null) {
      dispatch(loadCalendarSeries({ siteId }));
      fetchWindow(neededWindow);
    }
  }, [dispatch, siteId, fetchWindow, neededWindow]);

  const reloadTimer = useRef();
  const loadDataDebounced = useCallback(() => {
    clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(loadData, OCCURRENCE_FETCH_DEBOUNCE_MS);
  }, [loadData]);
  useEffect(() => () => clearTimeout(reloadTimer.current), []);

  const navigationTimer = useRef();
  useEffect(() => {
    if (siteId == null || isWindowCovered(fetchedWindow, neededWindow)) {
      return undefined;
    }
    clearTimeout(navigationTimer.current);
    navigationTimer.current = setTimeout(() => fetchWindow(neededWindow), OCCURRENCE_FETCH_DEBOUNCE_MS);
    return () => clearTimeout(navigationTimer.current);
  }, [siteId, fetchedWindow, neededWindow, fetchWindow]);

  const events = useMemo(
    () => occurrencesToEvents(occurrences, { timezone, resourceIdByRelayId }),
    [occurrences, timezone, resourceIdByRelayId],
  );

  return { events, occurrences, timezone, loadData, loadDataDebounced };
}
